/**
 * useApiCall Hook
 * 
 * Standardiserad API-anrop med felhantering och toast-notifieringar.
 * Ersätter duplicerade try-catch mönster för mutationer (POST, PUT, DELETE).
 * 
 * Användning:
 * const { execute, loading } = useApiCall<User>();
 * await execute(() => api.post('/api/users', userData), {
 *   successMessage: 'Användare skapad',
 *   errorMessage: 'Kunde inte skapa användare',
 * });
 */

import { useState, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useLanguage } from '@/store/languageStore';

export interface UseApiCallOptions {
  /** Show success toast with this message */
  successMessage?: string;
  /** Show error toast with this message (or use API error) */
  errorMessage?: string;
  /** Callback on success */
  onSuccess?: (data: any) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
  /** Show loading toast */
  showLoadingToast?: boolean;
  /** Loading toast message */
  loadingMessage?: string;
  /** Throw error instead of catching (for form handlers) */
  throwOnError?: boolean;
}

export interface UseApiCallResult<T> {
  /** Execute the API call */
  execute: (
    apiCall: () => Promise<any>,
    options?: UseApiCallOptions
  ) => Promise<T | null>;
  /** Loading state */
  loading: boolean;
  /** Last error */
  error: Error | null;
  /** Last successful data */
  data: T | null;
  /** Reset state */
  reset: () => void;
}

export function useApiCall<T = any>(): UseApiCallResult<T> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<T | null>(null);
  const { language } = useLanguage();
  
  const isMountedRef = useRef(true);

  const execute = useCallback(async (
    apiCall: () => Promise<any>,
    options: UseApiCallOptions = {}
  ): Promise<T | null> => {
    const {
      successMessage,
      errorMessage,
      onSuccess,
      onError,
      showLoadingToast = false,
      loadingMessage,
      throwOnError = false,
    } = options;

    let toastId: string | undefined;

    try {
      setLoading(true);
      setError(null);

      if (showLoadingToast) {
        toastId = toast.loading(loadingMessage || (language === 'sv' ? 'Laddar...' : 'Loading...'));
      }

      const response = await apiCall();
      
      if (!isMountedRef.current) return null;

      // Handle standard API response format
      let resultData: T;
      if (response?.data?.success !== undefined) {
        if (!response.data.success) {
          throw new Error(response.data.message || (language === 'sv' ? 'Något gick fel' : 'Something went wrong'));
        }
        resultData = response.data.data;
      } else {
        resultData = response?.data ?? response;
      }

      setData(resultData);

      if (toastId) {
        toast.dismiss(toastId);
      }

      if (successMessage) {
        toast.success(successMessage);
      }

      onSuccess?.(resultData);
      return resultData;

    } catch (err: any) {
      if (!isMountedRef.current) return null;

      const errorObj = err instanceof Error ? err : new Error(err.message || 'Unknown error');
      setError(errorObj);

      if (toastId) {
        toast.dismiss(toastId);
      }

      // Extract error message from API response
      const apiErrorMessage = err.response?.data?.message || err.response?.data?.error;
      const displayMessage = errorMessage || apiErrorMessage || errorObj.message;

      toast.error(displayMessage);
      onError?.(errorObj);

      if (throwOnError) {
        throw errorObj;
      }

      return null;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [language]);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  return {
    execute,
    loading,
    error,
    data,
    reset,
  };
}

/**
 * Pre-configured API call hooks for common operations
 */
export function useApiCreate<T = any>() {
  const { language } = useLanguage();
  const apiCall = useApiCall<T>();

  const create = useCallback(async (
    endpoint: string,
    data: any,
    options: Omit<UseApiCallOptions, 'successMessage' | 'errorMessage'> & {
      entityName?: string;
    } = {}
  ) => {
    const { entityName = '', ...rest } = options;
    const successMsg = language === 'sv' 
      ? `${entityName} skapad`.trim()
      : `${entityName} created`.trim();
    const errorMsg = language === 'sv'
      ? `Kunde inte skapa ${entityName}`.trim().toLowerCase()
      : `Could not create ${entityName}`.trim().toLowerCase();

    const api = (await import('@/services/api/client')).default;
    return apiCall.execute(
      () => api.post(endpoint, data),
      { successMessage: successMsg, errorMessage: errorMsg, ...rest }
    );
  }, [apiCall, language]);

  return { ...apiCall, create };
}

export function useApiUpdate<T = any>() {
  const { language } = useLanguage();
  const apiCall = useApiCall<T>();

  const update = useCallback(async (
    endpoint: string,
    data: any,
    options: Omit<UseApiCallOptions, 'successMessage' | 'errorMessage'> & {
      entityName?: string;
    } = {}
  ) => {
    const { entityName = '', ...rest } = options;
    const successMsg = language === 'sv'
      ? `${entityName} uppdaterad`.trim()
      : `${entityName} updated`.trim();
    const errorMsg = language === 'sv'
      ? `Kunde inte uppdatera ${entityName}`.trim().toLowerCase()
      : `Could not update ${entityName}`.trim().toLowerCase();

    const api = (await import('@/services/api/client')).default;
    return apiCall.execute(
      () => api.put(endpoint, data),
      { successMessage: successMsg, errorMessage: errorMsg, ...rest }
    );
  }, [apiCall, language]);

  return { ...apiCall, update };
}

export function useApiDelete<T = any>() {
  const { language } = useLanguage();
  const apiCall = useApiCall<T>();

  const remove = useCallback(async (
    endpoint: string,
    options: Omit<UseApiCallOptions, 'successMessage' | 'errorMessage'> & {
      entityName?: string;
    } = {}
  ) => {
    const { entityName = '', ...rest } = options;
    const successMsg = language === 'sv'
      ? `${entityName} borttagen`.trim()
      : `${entityName} deleted`.trim();
    const errorMsg = language === 'sv'
      ? `Kunde inte ta bort ${entityName}`.trim().toLowerCase()
      : `Could not delete ${entityName}`.trim().toLowerCase();

    const api = (await import('@/services/api/client')).default;
    return apiCall.execute(
      () => api.delete(endpoint),
      { successMessage: successMsg, errorMessage: errorMsg, ...rest }
    );
  }, [apiCall, language]);

  return { ...apiCall, remove };
}

export default useApiCall;
